import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CoachService } from "../services";
import { useMemo } from "react";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { useAccountSettings } from "./useAccountSettings";
import { WebSettings } from "../interfaces";
import { PlayerSegmentationType } from "../services/bedrock";

export interface SegmentationOption {
  key: string;
  text: string;
  value: string;
  label: string;
}

export const useSegmentation = (
  types: PlayerSegmentationType[] = ["REGION", "BRANCH", "STATE", "COST_CENTER", "INDUSTRY_ROLE"]
) => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();
  const { hasAccountSetting } = useAccountSettings(coachId);
  const hasCostCenter = hasAccountSetting(WebSettings.COST_CENTER);

  const { data: regions = [] } = useQuery({
    queryKey: ["coachSegmentation", coachId, "regions"],
    queryFn: () => CoachService.regions(coachId!),
    enabled: !!coachId && types.includes("REGION"),
    placeholderData: [],
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["coachSegmentation", coachId, "branches"],
    queryFn: () => CoachService.branches(coachId!),
    enabled: !!coachId && types.includes("BRANCH"),
    placeholderData: [],
  });

  const { data: states = [] } = useQuery({
    queryKey: ["coachSegmentation", coachId, "states"],
    queryFn: () => CoachService.states(coachId!),
    enabled: !!coachId && types.includes("STATE"),
    placeholderData: [],
  });

  const { data: costCenters = [] } = useQuery({
    queryKey: ["coachSegmentation", coachId, "costCenters"],
    queryFn: () => CoachService.costCenters(coachId!),
    enabled: !!coachId && hasCostCenter && types.includes("COST_CENTER"),
    placeholderData: [],
  });

  const { data: industryRoles = [] } = useQuery({
    queryKey: ["coachSegmentation", coachId, "industryRoles"],
    queryFn: () => CoachService.industryRoles(coachId!),
    enabled: !!coachId && types.includes("INDUSTRY_ROLE"),
    placeholderData: [],
  });

  const branchOptions = useMemo(() => {
    return branches.map(branch => ({
      key: branch.branchId,
      value: branch.branchId,
      text: branch.name,
      label: "Branch",
    }));
  }, [branches]);

  const regionOptions = useMemo(() => {
    return regions.map(region => ({
      key: region.regionId,
      value: region.regionId,
      text: region.name,
      label: "Region",
    }));
  }, [regions]);

  const costCenterOptions = useMemo(() => {
    return costCenters.map(c => ({
      key: c.costCenterId,
      value: c.costCenterId,
      text: c.name,
      label: "Cost Center",
    }));
  }, [costCenters]);

  const industryRoleOptions = useMemo(() => {
    return industryRoles.map(ir => ({
      key: ir.industryRoleId,
      value: ir.industryRoleId,
      text: ir.name,
      label: "Role",
    }));
  }, [industryRoles]);

  const stateOptions = useMemo(() => {
    return states.map(s => ({
      key: s.stateId,
      value: s.stateId,
      text: s.stateCode,
      label: "State",
    }));
  }, [states]);

  const segmentationOptions = useMemo(() => {
    return [...regionOptions, ...branchOptions, ...stateOptions, ...costCenterOptions, ...industryRoleOptions];
  }, [regionOptions, branchOptions, stateOptions, costCenterOptions, industryRoleOptions]);

  const getOptions = (type: PlayerSegmentationType): SegmentationOption[] => {
    switch (type) {
      case "REGION":
        return regionOptions;
      case "BRANCH":
        return branchOptions;
      case "STATE":
        return stateOptions;
      case "COST_CENTER":
        return costCenterOptions;
      case "INDUSTRY_ROLE":
        return industryRoleOptions;
      default:
        return [];
    }
  };

  const clearSegmentationCache = () => {
    queryClient.invalidateQueries({ queryKey: ["coachSegmentation", coachId] }).catch(console.error);
  };

  return {
    regions,
    regionOptions,
    branches,
    branchOptions,
    states,
    stateOptions,
    industryRoles,
    industryRoleOptions,
    costCenters,
    costCenterOptions,
    getOptions,
    segmentationOptions,
    clearSegmentationCache,
  };
};
